import { useApolloClient, useMutation } from '@apollo/client';
import { useSetAtom } from 'jotai';
import moment from 'moment';
import React from 'react';
import { isAddOrShareMenuOpenAtom } from './controlBarMenuAtoms';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Toast from '../components/Toast';
import { EditorActions } from '../editor/editorSlice';
import { gql } from '../graphql/generated/gql';
import { PostLinkAccess } from '../graphql/generated/graphql';
import { UIActions } from '../ui/uiSlice';

const CACHED_POST_QUERY = gql(/** @complexity 3 */ `
  query UsePublishPostCachedPost($id: ID!) {
    # eslint-disable-next-line @graphql-eslint/require-selections
    post(id: $id) {
      topics {
        id
      }
    }
  }
`);

const PUBLISH_POST_MUTATION = gql(/** @complexity 2 */ `
  mutation updatePost($id: ID!, $published: Boolean!) {
    post: updatePost(id: $id, published: $published) {
      id
      publishedAt
      linkAccess
    }
  }
`);

const usePublishPost = (id: string) => {
  const client = useApolloClient();
  const dispatch = useAppDispatch();
  const [updatePost] = useMutation(PUBLISH_POST_MUTATION);
  const setIsAddOrShareMenuOpen = useSetAtom(isAddOrShareMenuOpenAtom);

  const publishPost = async (published: boolean) => {
    const cachedPostResult = client.readQuery({
      query: CACHED_POST_QUERY,
      variables: { id },
    });

    const { data } = await updatePost({
      variables: { id, published },
      optimisticResponse: {
        __typename: 'RootMutationType',
        post: {
          __typename: 'Post',
          id,
          linkAccess: PostLinkAccess.Internal,
          publishedAt: published ? moment().toISOString() : null,
        },
      },
    });

    const hasTopic =
      cachedPostResult && cachedPostResult.post.topics.length > 0;

    if (data?.post) {
      dispatch(EditorActions.readOnly(published));
    }

    if (published && !hasTopic) {
      dispatch(
        UIActions.pushToast(
          (toastProps: { onDismiss: () => void }) => (
            <Toast
              message="Your teammates can now access and search for this post. Add a topic to help them more easily discover it."
              onDismiss={toastProps.onDismiss}
              onPrimaryClick={() => {
                dispatch(EditorActions.readOnly(false));
                setTimeout(() => {
                  setIsAddOrShareMenuOpen(true);
                }, 1);
                toastProps.onDismiss();
              }}
              onSecondaryClick={toastProps.onDismiss}
              primaryText="Add topics"
              secondaryText="No, thanks"
              title="Post published without topics"
            />
          ),
          7000,
        ),
      );
    }
  };

  return publishPost;
};

export default usePublishPost;
